/**
 * Typography token definitions.
 * Centralized font weights and line heights for consistent text rhythm.
 */

export const typographyTokens = {
    fontSizes: {
        none: '0px',
        hairline: '1px',
        micro: '9px',
        xxs: '10px',
        xs: '11px',
        caption: '12px',
        sm: '13px',
        body: '14px',
        md: '15px',
        base: '16px',
        lg: '17px',
        xl: '18px',
        h6: '20px',
        h5Small: '22px',
        h5: '24px',
        displaySmall: '25px',
        h4: '28px',
        h3: '32px',
        h2: '40px',
        h1: '48px',
        display: '70px',
    },
    fontWeights: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeights: {
        tight: 1.2,      // 20% compression, for compact layouts
        normal: 1.5,     // 50% expansion, standard body text
        relaxed: 1.75,   // 75% expansion, improved readability
        loose: 2,        // 100% expansion, maximum readability
    },
} as const;

export const typography = {
        fontFamily: 'Open Sans',
        fontWeightLight: typographyTokens.fontWeights.regular,
        fontWeightRegular: typographyTokens.fontWeights.regular,
        fontWeightMedium: typographyTokens.fontWeights.medium,
        fontWeightBold: typographyTokens.fontWeights.bold,
        
        // Display styles for large headings
        h1: {
            fontSize: '48px',
            fontWeight: typographyTokens.fontWeights.bold,
            lineHeight: typographyTokens.lineHeights.tight,
            letterSpacing: '-0.5px',
        },
        h2: {
            fontSize: '40px',
            fontWeight: typographyTokens.fontWeights.bold,
            lineHeight: typographyTokens.lineHeights.tight,
            letterSpacing: '-0.25px',
        },
        h3: {
            fontSize: '32px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.tight,
            letterSpacing: '0px',
        },
        h4: {
            fontSize: '28px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.tight,
            letterSpacing: '0px',
        },
        h5: {
            fontSize: '24px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0px',
        },
        h6: {
            fontSize: '20px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0px',
        },
        
        // Primary body text styles
        body1: {
            fontSize: '16px',
            fontWeight: typographyTokens.fontWeights.regular,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0px',
        },
        body2: {
            fontSize: '14px',
            fontWeight: typographyTokens.fontWeights.regular,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0px',
        },
        
        // Subtitle and label styles
        subtitle1: {
            fontSize: '16px',
            fontWeight: typographyTokens.fontWeights.medium,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0.15px',
        },
        subtitle2: {
            fontSize: '14px',
            fontWeight: typographyTokens.fontWeights.medium,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0.1px',
        },
        
        // Button and small text styles
        button: {
            fontSize: '14px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0.5px',
            textTransform: 'none' as const, // Override MUI default uppercase
        },
        caption: {
            fontSize: '12px',
            fontWeight: typographyTokens.fontWeights.regular,
            lineHeight: typographyTokens.lineHeights.normal,
            letterSpacing: '0.3px',
        },
        overline: {
            fontSize: '12px',
            fontWeight: typographyTokens.fontWeights.semibold,
            lineHeight: typographyTokens.lineHeights.tight,
            letterSpacing: '1px',
            textTransform: 'uppercase' as const,
        },
        
        // Legacy title style maintained for backward compatibility
        title: {
            font: 'normal normal 600 28px/38px Open Sans;',
        },
    };
